export const ButtonStyle = {
  baseStyle: {
    fontWeight: 600,
    fontFamily: "'Poppins', 'Inter', 'Helvetica Neue', Arial, sans-serif",
    textTransform: 'none',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },

  variants: {
    // Clean native variants for new code
    solid: {
      // accent.500 (#B65A1C): white labels need 4.5:1; 400 (#D16A24) is 3.6:1 (RSW-613).
      bg: 'accent.500',
      color: 'white',
      _hover: { bg: 'accent.600', _disabled: { bg: 'accent.500' } },
      _active: { bg: 'accent.700' },
    },
    outline: {
      bg: 'transparent',
      color: 'brand.500',
      border: '1.5px solid',
      borderColor: 'brand.500',
      _hover: { bg: 'brand.50' },
      _active: { bg: 'brand.100' },
    },
    ghost: {
      bg: 'transparent',
      color: 'brand.500',
      _hover: { bg: 'brand.50' },
      _active: { bg: 'brand.100' },
    },
    subtle: {
      bg: 'accent.50',
      color: 'accent.600',
      border: '1px solid',
      borderColor: 'accent.200',
      _hover: { bg: 'accent.100' },
    },
    download: {
      bg: 'accent.50',
      textTransform: 'uppercase',
      fontSize: '13px',
      fontWeight: 600,
      color: 'accent.600',
      height: { base: '36px', lg: '40px' },
      border: '1px solid',
      borderColor: 'accent.200',
      borderRadius: '8px',
      _hover: { bg: 'accent.600', color: 'white' },
    },

    // Legacy wrapper variant tree (kept during migration)
    wrapper: {
      primary: {
        bg: 'accent.500',
        color: 'white',
        borderRadius: '8px',
        _hover: { bg: 'accent.600' },
        _focus: { borderColor: 'accent.400' },
      },
      careerSummaryWork: { margin: ['0 0 8px', '0'] },
      careerSummaryContinue: { margin: ['0', '0 16px 0 0'] },
      singleSectionMenu: { marginTop: '16px', float: 'left' },
      secondary: {
        bg: 'brand.500',
        color: 'white',
        borderRadius: '8px',
        _hover: { bg: 'brand.700' },
        _focus: { borderColor: 'brand.500' },
      },
      newResume: {
        width: 'fit-content',
        marginBottom: '1rem',
        bg: 'brand.500',
        color: 'white',
        borderRadius: '8px',
        _hover: { bg: 'brand.700' },
      },
      done: {
        bg: 'transparent',
        textDecoration: 'underline',
        color: 'brand.500',
        fontWeight: 'normal',
        _hover: { textDecoration: 'none' },
      },
      tertiary: {
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'brand.border',
        color: 'brand.text',
        borderRadius: '8px',
        _hover: { bg: 'gray.100' },
      },
      backButton: { marginRight: [0, '24px'], marginTop: ['12px', 0] },
      previewClose: { margin: ['0', '0 16px 0 0'] },
      cancel: { margin: ['0', '0 16px 0 0'] },
      start: {
        borderColor: 'brand.border',
        color: 'brand.text',
        borderRadius: '8px',
      },
      quaternary: {
        bg: 'accent.500',
        color: 'white',
        borderRadius: '8px',
        _hover: { bg: 'accent.600' },
      },
      quinary: {
        bg: 'accent.500',
        color: 'white',
        borderRadius: '8px',
        _hover: { bg: 'accent.600' },
      },
      empty: {
        padding: 0,
        margin: 0,
        bg: 'transparent',
        border: 'none',
        width: 'auto',
        height: 'auto',
      },
      expertiseSearch: {
        margin: 0,
        padding: '0',
        height: '20px',
        width: '20px',
        minWidth: '20px',
        border: '1px solid',
        borderColor: 'brand.border',
        bg: 'white',
        borderRadius: '4px',
        _hover: { bg: 'gray.200', color: 'white' },
      },
      update: {
        bg: 'gray.100',
        color: 'brand.text',
        borderRadius: '8px',
        _hover: { bg: 'gray.300', color: 'brand.text' },
      },
      text: {
        bg: 'transparent',
        color: 'red.500',
        fontSize: 14,
        fontWeight: 600,
        height: 'auto',
        width: 'auto',
        padding: '0',
        margin: '0',
        _hover: { textDecoration: 'underline' },
      },
      protip: {
        fontSize: '20px',
        color: 'brand.primary',
        padding: '0px',
        width: 'fit-content',
        height: { base: '20px', lg: '28px' },
        _hover: { color: 'accent.600' },
      },
      bulletRowOpen: { width: 'fit-content', fontSize: [30, 24], marginTop: '12px' },
      bulletTag: {
        bg: 'transparent',
        border: '1px solid',
        width: 'fit-content',
        padding: '0 .3rem',
        borderColor: 'brand.border',
        color: 'brand.text',
        margin: '12px 8px 0 0',
        borderRadius: '6px',
        _hover: { borderColor: 'accent.400', bg: 'accent.50' },
        _focus: { borderColor: 'accent.400', bg: 'accent.50' },
      },
      bulletTagActive: {
        bg: 'accent.50',
        width: 'fit-content',
        padding: '0 .3rem',
        border: '1px solid',
        borderColor: 'accent.400',
        color: 'brand.text',
        margin: '12px 8px 0 0',
        borderRadius: '6px',
        _hover: { bg: 'accent.100' },
      },
      icon: { width: 8, height: 8, border: 'none', margin: '0' },
      logout: { fontSize: '30px' },
      sumNav: { width: 8, height: 8, marginLeft: [0, null, 3], marginBottom: [2] },
      add: { width: 'auto' },
      addDel: {
        padding: 0,
        margin: 0,
        bg: 'transparent',
        border: 'none',
        color: 'accent.400',
        fontSize: 22,
        fontWeight: 400,
        width: 'auto',
        height: 'auto',
        alignSelf: 'baseline',
        paddingTop: '8px',
      },
      search: { width: 'auto', bg: 'transparent', color: 'brand.text.secondary' },
      download: {
        bg: 'accent.50',
        textTransform: 'uppercase',
        fontSize: '13px',
        color: 'accent.600',
        height: { base: '36px', lg: '40px' },
        border: '1px solid',
        borderColor: 'accent.200',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'flex-end',
        width: 'fit-content',
        marginRight: '.5rem',
        _hover: { bg: 'accent.600', color: 'white' },
      },
      menuDownload: { marginRight: '0px !important' },
      skillSearch: {
        maxHeight: 'auto',
        width: 'auto',
        bg: 'accent.500',
        color: 'white',
        borderRadius: '6px',
        margin: ['0 12px 8px 0', '0 8px 8px 8px'],
      },
    },
    defaultProps: { variants: 'solid' },
  },
};
